import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGetServiceCategoriesQuery } from "../redux/services/api";
import { parseDraftContent } from "../utils/excerpt";
import { useTranslation } from "../hooks/useTranslation";
import AOS from "aos";
import "aos/dist/aos.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const Services = () => {
  const navigate = useNavigate();
  const { t, isArabic } = useTranslation();
  const swiperRef = useRef(null);

  // Use RTK Query to fetch categories instead of services
  const { data: categoriesData, isLoading: loading } =
    useGetServiceCategoriesQuery();
  const categories =
    categoriesData?.categories?.filter(
      (category) => category && category.is_active
    ) || [];

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  // Ensure navigation stays enabled

  const handleCategoryClick = (categoryId) => {
    navigate(`/services?category=${categoryId}`);
  };

  const handleViewAllServices = () => {
    navigate("/services");
  };

  if (loading) {
    return (
      <section className="services">
        <div className="container">
          <h2 className="section-title">{t("services.title")}</h2>
          <div className="categories-container">
            <div className="loading-placeholder">
              <div className="loading-spinner"></div>
              <p>Loading categories...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (categories.length === 0) {
    return (
      <section className="services">
        <div className="container">
          <h2 className="section-title">{t("services.title")}</h2>
          <div className="categories-container">
            <div className="no-services">
              <p>No categories available at the moment.</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="services">
      <div className="container">
        <h2 className="section-title" data-aos="fade-up" data-aos-delay="100">
          {t("services.title")}
        </h2>
        <div className="categories-container">
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={0}
            slidesPerView={1}
            navigation={categories.length > 1}
            pagination={{
              enabled: categories.length > 1,
              clickable: true,
              dynamicBullets: false,
              type: "bullets",
            }}
            className="categories-carousel"
            dir={isArabic ? "rtl" : "ltr"}
            key={isArabic ? "rtl" : "ltr"}
          >
            {categories.map((category, index) => (
              <SwiperSlide key={category.id}>
                <div
                  className="category-card clickable-category-card"
                  data-aos="fade-up"
                  data-aos-delay={`${200 + index * 100}`}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  <div className="category-image-wrapper">
                    {category.image_url ? (
                      <img
                        src={category.image_url}
                        alt={
                          isArabic && category.title_ar
                            ? category.title_ar
                            : category.title
                        }
                        className="category-img"
                        onError={(e) => {
                          e.target.style.display = "none";
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = "flex";
                          }
                        }}
                      />
                    ) : (
                      <div className="category-image-placeholder">
                        <span>
                          {isArabic && category.title_ar
                            ? category.title_ar
                            : category.title}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="category-content">
                    <h3 className="category-title">
                      {isArabic && category.title_ar
                        ? category.title_ar
                        : category.title}
                    </h3>
                    {category.description || category.description_ar ? (
                      <div
                        className="category-description"
                        dangerouslySetInnerHTML={{
                          __html: parseDraftContent(
                            isArabic && category.description_ar
                              ? category.description_ar
                              : category.description
                          ),
                        }}
                      />
                    ) : null}
                    <button
                      className="btn mt-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryClick(category.id);
                      }}
                    >
                      {t("services.readMore")}
                    </button>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className="view-all-services">
          <button className="btn btn-secondary" onClick={handleViewAllServices}>
            {t("services.viewAll")}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
